import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import uniqBy from "lodash/uniqBy";
import styled from "styled-components";
import { AlertNotification, Loading } from "@commonsku/styles";

import { createAddItemLocation } from "../../actions";
import { createAddItemLocationPopup } from "../../actions/popup";
import { createFetchProduct } from "../../actions/product";
import { getItem, getItemLocations, getProduct } from "../../selectors/items";
import { type PodLocation } from "../../pod";
import { usePoDProductConfiguration } from "../../hooks/usePoDProductConfiguration";

import BasicLocationDropdownMenu from "./BasicLocationDropdownMenu";

const AddLocationLink = styled.a`
  padding: 1rem;
  font-weight: bold;
`;

type Option = {
  key: string;
  value: React.ReactNode;
  hideOnClick?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
};

interface Props {
  divisionId?: string;
  itemId: string;
  locked?: boolean;
  onOptionClick?: (location: PodLocation) => void;
  children?: React.ReactNode;
}

export default function PoDLocationMenu({
  divisionId,
  itemId,
  locked = false,
  onOptionClick,
  children,
  ...props
}: Props) {
  const dispatch = useDispatch();
  const location = useLocation();

  const item = useSelector((state) => getItem(state, { item_id: itemId }));
  const itemLocations = useSelector((state) => getItemLocations(state, { item_id: itemId }));
  const product = useSelector((state) => getProduct(state, { product_id: item?.parent_id }));

  const isShopApp = location.pathname.includes("/shop");

  useEffect(() => {
    if (!product && item?.parent_id) {
      dispatch(createFetchProduct(item.parent_id));
    }
  }, [dispatch, item?.parent_id, product]);

  const { productLocations, loading: configurationLoading } = usePoDProductConfiguration({
    extProductId: product?.ext_product_id,
    divisionId: divisionId || product?.division_id,
    enabled: Boolean(product?.ext_product_id), // optional check to only fetch configuration when PoD external product exists
  });

  const loading = (!product && Boolean(item?.parent_id)) || configurationLoading;

  const extLocationIdSet = new Set(itemLocations.map((row) => String(row.ext_location_id)));
  const hasSupportedDecoration = productLocations.some(({ decorations = [] }) => decorations.length > 0);
  const availableLocations = productLocations.filter(({ pod_location_id, decorations = [] }) =>
    !extLocationIdSet.has(String(pod_location_id)) && decorations.length > 0,
  );

  const locationOptions: Option[] = uniqBy(
    availableLocations.map((podLocation) => ({
      key: podLocation.pod_location_id,
      value: podLocation.name || podLocation.pod_location_id,
      hideOnClick: true,
      onClick: () => {
        if (locked) {
          return;
        }

        if (onOptionClick) {
          onOptionClick(podLocation);
          return;
        }

        dispatch(createAddItemLocation(itemId, null, null, null, {
          // reuse existing PS plumbing for ext_location_id assignment
          ps_location_id: podLocation.pod_location_id,
          location_name: podLocation.name || podLocation.pod_location_id,
        }));
      },
    })),
    (option) => option.key,
  );

  if (loading) {
    return <Loading />;
  }

  if (productLocations.length === 0 && isShopApp && !locked) {
    return (
      <AddLocationLink
        onClick={(e) => {
          e.preventDefault();
          dispatch(createAddItemLocationPopup(itemId));
        }}
      >
        + Decoration Location
      </AddLocationLink>
    );
  }

  if (productLocations.length && !hasSupportedDecoration) {
    return (
      <div style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
        <AlertNotification alertType="warn">
          There are no supported decorations for this print on demand item.
        </AlertNotification>
      </div>
    );
  }

  if (locked || locationOptions.length === 0) {
    return null;
  }

  return (
    <BasicLocationDropdownMenu options={locationOptions} {...props}>
      {children}
    </BasicLocationDropdownMenu>
  );
}
